import { logger } from "../utils/logger";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { 
  Calendar, 
  Trophy, 
  Users, 
  Clock, 
  MapPin, 
  ChevronRight,
  Star,
  Spade,
  Heart,
  Diamond,
  Club,
  DollarSign
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Floating Chip Component
const FloatingChip = ({ className, delay = 0, duration = 6 }) => (
  <div 
    className={`absolute ${className}`}
    style={{ animationDelay: `${delay}s`, animationDuration: `${duration}s` }}
  >
    <div className="relative">
      <div className="w-8 h-8 md:w-12 md:h-12 rounded-full bg-gradient-to-br from-gold via-gold-light to-gold-dark border-2 border-gold-light shadow-lg animate-gold-glow">
        <div className="absolute inset-1 rounded-full border border-dashed border-gold-dark/50" />
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-black font-bold text-xs">$</span>
        </div>
      </div>
    </div>
  </div>
);

// Floating Card Component  
const FloatingCard = ({ suit, className, delay = 0 }) => {
  const suitMap = { spade: Spade, heart: Heart, diamond: Diamond, club: Club };
  const SuitIcon = suitMap[suit] || Club;
  const color = (suit === 'heart' || suit === 'diamond') ? 'text-red-500' : 'text-white';
  
  return (
    <div 
      className={`absolute ${className}`}
      style={{ animationDelay: `${delay}s` }}
    >
      <div className="relative w-10 h-14 md:w-14 md:h-20 bg-white/10 backdrop-blur-sm rounded-lg border border-white/20 flex items-center justify-center shadow-xl">
        <SuitIcon className={`w-6 h-6 md:w-8 md:h-8 ${color}`} />
      </div>
    </div>
  );
};

// Jackpot Ticker Component
const JackpotTicker = ({ amount }) => {
  const [displayAmount, setDisplayAmount] = useState(amount - 500);
  
  useEffect(() => {
    const target = amount;
    const duration = 2000;
    const steps = 60;
    const startVal = target - 500;
    const increment = (target - startVal) / steps;
    let current = startVal;
    
    const timer = setInterval(() => {
      current += increment;
      if (current >= target) {
        setDisplayAmount(target);
        clearInterval(timer);
      } else {
        setDisplayAmount(Math.floor(current));
      }
    }, duration / steps);
    
    return () => clearInterval(timer);
  }, [amount]);
  
  return (
    <div className="inline-flex items-center gap-2">
      <DollarSign className="w-8 h-8 md:w-10 md:h-10 text-gold animate-gold-glow" />
      <span className="font-heading text-4xl md:text-5xl lg:text-6xl text-gold animate-ticker-pulse">
        {displayAmount.toLocaleString()}
      </span>
    </div>
  );
};

export default function Home() {
  const [events, setEvents] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [homepage, setHomepage] = useState(null);
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [jackpot, setJackpot] = useState(15750);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [eventsRes, promosRes, leaderRes, homeRes] = await Promise.all([
          axios.get(`${API}/events`),
          axios.get(`${API}/promotions`),
          axios.get(`${API}/leaderboard`),
          axios.get(`${API}/homepage`)
        ]);
        setEvents(eventsRes.data.slice(0, 3));
        setPromotions(promosRes.data);
        setLeaderboard(leaderRes.data.slice(0, 5));
        setHomepage(homeRes.data);
      } catch (err) {
        logger.error("Error fetching data:", err);
      }
    };
    fetchData();
  }, []);

  // Countdown timer for next Saturday
  useEffect(() => {
    const getNextSaturday = () => {
      const now = new Date();
      const dayOfWeek = now.getDay();
      const daysUntilSat = (6 - dayOfWeek + 7) % 7 || 7;
      const nextSat = new Date(now);
      nextSat.setDate(now.getDate() + daysUntilSat);
      nextSat.setHours(16, 0, 0, 0);
      return nextSat;
    };

    const updateCountdown = () => {
      const target = getNextSaturday();
      const now = new Date();
      const diff = target - now;

      if (diff > 0) {
        setCountdown({
          days: Math.floor(diff / (1000 * 60 * 60 * 24)),
          hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((diff % (1000 * 60)) / 1000),
        });
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ 
            backgroundImage: "url('https://images.pexels.com/photos/18341169/pexels-photo-18341169.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')"
          }}
        />
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/40" />
        
        {/* Enhanced Floating Particles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Floating Poker Chips */}
          <FloatingChip className="top-[15%] left-[8%] animate-float" delay={0} duration={7} />
          <FloatingChip className="top-[25%] right-[12%] animate-float-reverse" delay={1.5} duration={8} />
          <FloatingChip className="bottom-[30%] left-[15%] animate-float-slow" delay={0.5} duration={9} />
          <FloatingChip className="bottom-[20%] right-[8%] animate-float" delay={2} duration={7} />
          <FloatingChip className="top-[45%] left-[5%] animate-float-reverse" delay={3} duration={10} />
          
          {/* Floating Cards */}
          <FloatingCard suit="spade" className="top-[20%] left-[20%] animate-float-slow" delay={0.5} />
          <FloatingCard suit="heart" className="top-[35%] right-[18%] animate-float" delay={1} />
          <FloatingCard suit="diamond" className="bottom-[35%] left-[12%] animate-float-reverse" delay={2} />
          <FloatingCard suit="club" className="bottom-[25%] right-[15%] animate-float-slow" delay={1.5} />
          
          {/* Gold Sparkles */}
          <div className="absolute top-[10%] left-[30%] w-2 h-2 rounded-full bg-gold animate-sparkle" style={{ animationDelay: "0s" }} />
          <div className="absolute top-[50%] left-[25%] w-1.5 h-1.5 rounded-full bg-gold animate-sparkle" style={{ animationDelay: "0.5s" }} />
          <div className="absolute top-[30%] right-[25%] w-2 h-2 rounded-full bg-gold animate-sparkle" style={{ animationDelay: "1s" }} />
          <div className="absolute bottom-[40%] right-[30%] w-1 h-1 rounded-full bg-gold animate-sparkle" style={{ animationDelay: "1.5s" }} />
          <div className="absolute top-[60%] right-[20%] w-1.5 h-1.5 rounded-full bg-gold animate-sparkle" style={{ animationDelay: "2s" }} />
          <div className="absolute bottom-[30%] left-[35%] w-2 h-2 rounded-full bg-gold animate-sparkle" style={{ animationDelay: "0.8s" }} />
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-5xl mx-auto px-4 text-center pt-20">
          <div className="animate-fade-in">
            <p className="text-gold uppercase tracking-[0.3em] text-base md:text-lg lg:text-xl mb-6 font-accent font-semibold drop-shadow-[0_0_10px_rgba(212,175,55,0.5)]">
              Veteran Owned & Operated
            </p>
            <h1 className="font-heading text-5xl sm:text-6xl md:text-7xl lg:text-8xl text-white mb-6 tracking-tight">
              San Marcos<br />
              <span className="gold-gradient">Social Club</span>
            </h1>
            <p className="text-white/70 text-lg md:text-xl max-w-2xl mx-auto mb-10 font-body">
              {homepage?.hero_description || "The Premier Poker Room in San Marcos. Dedicated to providing a welcoming, fun, and safe environment for poker players of all skill levels."}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/membership"
                className="bg-gold text-black font-bold uppercase tracking-wider px-8 py-4 text-sm hover:bg-gold-dark transition-all duration-300 hover:scale-105 shadow-gold"
                data-testid="hero-cta-membership"
              >
                Become a Member
              </Link>
              <Link
                to="/tournaments"
                className="border border-gold text-gold font-bold uppercase tracking-wider px-8 py-4 text-sm hover:bg-gold/10 transition-all duration-300"
                data-testid="hero-cta-tournaments"
              >
                View Tournament Schedule
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-1">
            <div className="w-1.5 h-3 bg-gold rounded-full animate-pulse" />
          </div>
        </div>
      </section>

      {/* Countdown Section */}
      <section className="py-16 bg-gradient-to-b from-black to-casino-dark" data-testid="countdown-section">
        <div className="max-w-7xl mx-auto px-4">
          <div className="glass-card p-8 md:p-12">
            <div className="text-center mb-8">
              <p className="text-gold uppercase tracking-widest text-sm mb-2">Next Tournament</p>
              <h2 className="font-heading text-3xl md:text-4xl text-white">Saturday $1K Guarantee</h2>
            </div>
            <div className="grid grid-cols-4 gap-4 max-w-xl mx-auto">
              {[
                { value: countdown.days, label: "Days" },
                { value: countdown.hours, label: "Hours" },
                { value: countdown.minutes, label: "Mins" },
                { value: countdown.seconds, label: "Secs" },
              ].map((item) => (
                <div key={item.label} className="text-center">
                  <div className="bg-black/50 border border-gold/20 rounded-lg p-4 mb-2">
                    <span className="font-heading text-3xl md:text-5xl text-gold" data-testid={`countdown-${item.label.toLowerCase()}`}>
                      {String(item.value).padStart(2, "0")}
                    </span>
                  </div>
                  <span className="text-white/60 text-xs uppercase tracking-wider">{item.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Prize Pool / Jackpot Ticker */}
      <section className="py-12 bg-gradient-to-b from-casino-dark to-casino-black relative overflow-hidden" data-testid="jackpot-section">
        {/* Background Glow Effects */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[200px] bg-gold/10 rounded-full blur-3xl" />
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-4">
          <div className="text-center">
            <div className="inline-block">
              <div className="flex items-center justify-center gap-3 mb-3">
                <div className="h-px w-12 bg-gradient-to-r from-transparent to-gold" />
                <Trophy className="w-6 h-6 text-gold animate-gold-glow" />
                <p className="text-gold uppercase tracking-[0.3em] text-sm font-accent">Monthly Prize Pool</p>
                <Trophy className="w-6 h-6 text-gold animate-gold-glow" />
                <div className="h-px w-12 bg-gradient-to-l from-transparent to-gold" />
              </div>
              
              <div className="relative">
                {/* Glowing Border */}
                <div className="absolute -inset-4 bg-gradient-to-r from-gold/20 via-gold/40 to-gold/20 rounded-2xl blur-xl opacity-50" />
                
                <div className="relative bg-black/60 backdrop-blur-md border-2 border-gold/50 rounded-xl px-8 py-6 md:px-12 md:py-8">
                  <JackpotTicker amount={jackpot} />
                  <p className="text-white/60 text-sm mt-3">+ Growing Daily</p>
                </div>
              </div>
              
              <p className="text-white/50 text-xs mt-4 uppercase tracking-wider">
                Across All Tournament Guarantees
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Promotion */}
      {promotions.length > 0 && (
        <section className="py-20 bg-casino-dark" data-testid="promotion-section">
          <div className="max-w-7xl mx-auto px-4">
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-casino-red/20 to-gold/10 border border-gold/20">
              <div className="absolute top-0 right-0 w-64 h-64 bg-gold/5 rounded-full blur-3xl" />
              <div className="relative p-8 md:p-12">
                <div className="flex items-center gap-2 mb-4">
                  <Star className="w-5 h-5 text-gold" />
                  <span className="text-gold uppercase tracking-widest text-sm">Featured Promotion</span>
                </div>
                <h2 className="font-heading text-3xl md:text-4xl text-white mb-4">
                  {promotions[0].title}
                </h2>
                <p className="text-white/70 max-w-2xl mb-6">
                  {promotions[0].description}
                </p>
                <div className="flex items-center gap-4 text-sm text-white/50">
                  <span>Jan 1 - May 31</span>
                  <span>•</span>
                  <span>Drawing June 6th</span>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Tournament Preview */}
      <section className="py-20 bg-casino-black" data-testid="tournaments-preview-section">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between mb-12">
            <div>
              <p className="text-gold uppercase tracking-widest text-sm mb-2">Weekly Schedule</p>
              <h2 className="font-heading text-3xl md:text-4xl text-white">Upcoming Tournaments</h2>
            </div>
            <Link
              to="/tournaments"
              className="hidden md:flex items-center gap-2 text-gold hover:text-gold-light transition-colors"
              data-testid="view-all-tournaments"
            >
              <span className="text-sm uppercase tracking-wider">View All</span>
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {events.map((event, index) => (
              <div 
                key={event.id} 
                className="glass-card p-6"
                style={{ animationDelay: `${index * 0.1}s` }}
                data-testid={`tournament-card-${index}`}
              >
                <div className="flex items-center gap-2 mb-4">
                  <Calendar className="w-5 h-5 text-gold" />
                  <span className="text-gold text-sm uppercase tracking-wider">{event.day}</span>
                </div>
                <h3 className="font-heading text-xl text-white mb-2">{event.event_name}</h3>
                {event.buy_in && (
                  <p className="text-white/60 text-sm mb-1">{event.buy_in}</p>
                )}
                {event.guarantee && (
                  <p className="text-gold font-semibold text-sm mb-4">{event.guarantee}</p>
                )}
                <div className="flex items-center gap-2 text-white/40 text-sm">
                  <Clock className="w-4 h-4" />
                  <span>{event.start_time}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 text-center md:hidden">
            <Link
              to="/tournaments"
              className="inline-flex items-center gap-2 text-gold hover:text-gold-light transition-colors"
            >
              <span className="text-sm uppercase tracking-wider">View Full Schedule</span>
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Leaderboard Preview */}
      <section className="py-20 bg-casino-dark" data-testid="leaderboard-preview-section">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between mb-12">
            <div>
              <p className="text-gold uppercase tracking-widest text-sm mb-2">Top Players</p>
              <h2 className="font-heading text-3xl md:text-4xl text-white">Leaderboard</h2>
            </div>
            <Link
              to="/leaderboard"
              className="hidden md:flex items-center gap-2 text-gold hover:text-gold-light transition-colors"
              data-testid="view-all-leaderboard"
            >
              <span className="text-sm uppercase tracking-wider">View Full Leaderboard</span>
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="glass-card overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="px-6 py-4 text-left text-xs uppercase tracking-wider text-gold">Rank</th>
                  <th className="px-6 py-4 text-left text-xs uppercase tracking-wider text-gold">Player</th>
                  <th className="px-6 py-4 text-right text-xs uppercase tracking-wider text-gold">Points</th>
                  <th className="px-6 py-4 text-right text-xs uppercase tracking-wider text-gold hidden sm:table-cell">Wins</th>
                </tr>
              </thead>
              <tbody>
                {leaderboard.map((entry, index) => (
                  <tr 
                    key={entry.id} 
                    className="border-b border-white/5"
                    data-testid={`leaderboard-row-${index}`}
                  >
                    <td className="px-6 py-4">
                      <span className={`font-heading text-xl ${index < 3 ? 'text-gold' : 'text-white/60'}`}>
                        #{entry.rank}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gold/20 flex items-center justify-center">
                          <Users className="w-5 h-5 text-gold" />
                        </div>
                        <span className="text-white font-medium">{entry.player_name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <span className="text-white font-semibold">{entry.points.toLocaleString()}</span>
                    </td>
                    <td className="px-6 py-4 text-right hidden sm:table-cell">
                      <span className="text-gold">{entry.wins}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Location Preview */}
      <section className="py-20 bg-casino-black" data-testid="location-section">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-gold uppercase tracking-widest text-sm mb-2">Visit Us</p>
              <h2 className="font-heading text-3xl md:text-4xl text-white mb-6">Find Your Seat</h2>
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-4">
                  <MapPin className="w-6 h-6 text-gold shrink-0" />
                  <p className="text-white/40 italic">Address Coming Soon</p>
                </div>
                <div className="flex items-center gap-4">
                  <Clock className="w-6 h-6 text-gold shrink-0" />
                  <p className="text-white/40 italic">Hours Coming Soon</p>
                </div>
              </div>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-gold text-black font-bold uppercase tracking-wider px-6 py-3 text-sm hover:bg-gold-dark transition-all duration-300 hover:scale-105 shadow-gold"
                data-testid="contact-cta"
              >
                Contact Us
                <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="aspect-video rounded-xl overflow-hidden border border-white/10 bg-white/5 flex items-center justify-center">
              <p className="text-white/30 italic text-lg">Map Coming Soon</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-b from-casino-dark to-black relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://images.pexels.com/photos/2555292/pexels-photo-2555292.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')] bg-cover bg-center opacity-10" />
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <Trophy className="w-16 h-16 text-gold mx-auto mb-8" />
          <h2 className="font-heading text-4xl md:text-5xl text-white mb-6">
            Ready to Join the Action?
          </h2>
          <p className="text-white/70 text-lg mb-10 max-w-2xl mx-auto">
            Whether you're learning how to play poker or you're an experienced pro, 
            San Marcos Social Club has something for everyone.
          </p>
          <Link
            to="/membership"
            className="inline-block bg-gold text-black font-bold uppercase tracking-wider px-10 py-5 text-sm hover:bg-gold-dark transition-all duration-300 hover:scale-105 shadow-gold-lg"
            data-testid="cta-membership"
          >
            Become a Member Today
          </Link>
        </div>
      </section>
    </div>
  );
}
